import { sortBy, values, intersection, maxBy } from 'lodash';
import { IShapeModel, IRectangle, ILayoutingData, IDiagramDefinition } from 'flux-definition';
import { isEmpty, omit } from 'lodash';
import ELK from '@creately/elkjs/lib/elk.bundled.js';
import MindmapLayouts from 'mindmap-layouts';
import { TreeUtils } from 'simple-tree-utils';
import { OrgChart } from 'd3-org-chart';
import { Logger, Rectangle } from 'flux-core';
import { DiagramModel } from 'apps/nucleus/src/base/diagram/model/diagram.mdl';
import { ConnectorModel } from 'apps/nucleus/src/base/shape/model/connector.mdl';
import { GluePointModel } from 'apps/nucleus/src/base/shape/model/gluepoint.mdl';
import { ShapeModel } from 'apps/nucleus/src/base/shape/model/shape.mdl';

/**
 * Layout class is to maintain all the native and 3rd party layouting
 * algorhythms like
 * ELK library https://www.eclipse.org/elk/reference/algorithms/org-eclipse-elk-layered.html used
 *
 * @author thisun
 * @since 2020-11-23
 */

export class Layouting {

    public static restriction: any;
    protected static elk = new ELK();
    protected static defaultPadding = [ 10, 10, 10, 10 ];

    /**
     * Apply the layouting algorithm to the passed shapes
     * @param shapes Array of shapes to apply the layouting
     * @param bounds The bounds the layouting shoud be fit into
     * @param layoutingData ILayoutingData
     * @param padding Padding to be applied to the bounds
     * @
     */
    // tslint:disable-next-line: member-ordering
    public static async apply(
        diagram: DiagramModel,
        shapes: any[],
        bounds: IRectangle,
        layoutingData: any,
        padding = [ 0, 0, 0, 0 ],
        allowFreeForm = true,
    ): Promise<IShapeModel[]> {
        bounds = Rectangle.from({
            x: bounds.x + padding[3],
            y: bounds.y + padding[0],
            width: bounds.width - padding[1] - padding[3],
            height: bounds.height - padding[0] - padding[2],
        });


        ( diagram as any ).$$resetSelection = true;
        // FIXME: disabling freeform for now
        // if ( allowFreeForm ) {
        //     shapes = shapes.filter(( s, i ) => {
        //         const shapeBounds = ( s as any ).bounds;
        //         const thresholdRect = ( bounds as Rectangle ).clone();
        //         thresholdRect.pad( 5 );
        //         if ( shapeBounds && thresholdRect.contains( shapeBounds ))  {
        //             return true;
        //         }
        //     });
        // }
        shapes = shapes.filter(( s: any, i ) => s && !( diagram as any )?.skipLayout( s.id ));


        if ( layoutingData.source === 'elk' ) {
            const { children, edges, ignoredEdges } = Layouting
                .prepareNodesForElk( diagram, shapes, layoutingData.options[ 'org.eclipse.elk.direction' ]);
            const relativeX = bounds.x; // + padding[3];
            // const relativeY = bounds.y; // - padding / 2;
            const relativeY = bounds.y; // + padding[0];
            const width = bounds.width; // - padding[1] - padding[3];
            const height = bounds.height; // - padding[0] - padding[2];
            const aspectRatio = layoutingData.aspectRatio === 'dynamic' ?
                ( width / height ) : layoutingData.aspectRatio || 1.6;

            const graph = {
                id: 'root', // not important
                children,
                edges,
                properties: {
                    'org.eclipse.elk.padding': `` + 0,
                    'org.eclipse.elk.algorithm': layoutingData.algorithm,
                    'elk.aspectRatio': `` + aspectRatio,
                    'org.eclipse.elk.rectpacking.widthApproximation.strategy': 'TARGET_WIDTH',
                    'org.eclipse.elk.rectpacking.widthApproximation.targetWidth':
                        `` + width,
                    ...layoutingData.options as any,
                } as any,
            } as any;

            const result = await Layouting.elk.layout( graph );
            // Update x, y shape's XY
            try {
                ignoredEdges.forEach( edge => {
                    edge.triggerAutolayout = Math.random();
                    const { h = {}, t = {}} = edge.path;
                    delete h.gluepointId;
                    delete h.gluepointLocked;
                    delete t.gluepointId;
                    delete t.gluepointLocked;
                });

                result.edges.forEach( edge => {
                    const connector = ( diagram as any ).shapes[ edge.id ] as any;
                    ( connector as any ).triggerAutolayout = Math.random();

                    if ( !edge.sections ) {
                        return;
                    }
                    Object.keys( connector.path ).forEach( key => {
                        const val = connector.path[ key ];
                        if ( val.pathAdjustedManually ) {
                            val.pathAdjustedManually = false;
                        }
                    });
                    const fromShape = ( diagram as any ).shapes[ edge.sources[ 0 ]];
                    const toShape = ( diagram as any ).shapes[ edge.targets[ 0 ]];
                    const _fromShape = result.children.find( c => c.id === edge.sources[ 0 ]);
                    const _toShape = result.children.find( c => c.id === edge.targets[ 0 ]);

                    const startPoint: any = edge.sections[ 0 ].startPoint;
                    const endPoint: any  = edge.sections[ 0 ].endPoint;

                    const gpX = ( startPoint.x - _fromShape.x ) / _fromShape.width;
                    const gpY = ( startPoint.y - _fromShape.y ) / _fromShape.height;
                    const gpstart: any = Object
                        .values( fromShape.gluepoints ).find(( gp: any ) =>
                            ( gpX > 0 && gpX < 1 && gp.y === gpY ) ||
                            ( gp.x === gpX && gpY > 0 && gpY < 1 ));

                    const fromEp = connector.getFromEndpoint( diagram as any );
                    if ( gpstart && fromEp.gluepoint ) {
                        ( connector as any ).fromGPAutolayout = `${fromShape.id}-${( gpstart as any ).id}`;
                    }

                    const gpXe = ( endPoint.x - _toShape.x ) / toShape.width;
                    const gpYe = ( endPoint.y - _toShape.y ) / toShape.height;
                    const gpend: any = Object
                        .values( toShape.gluepoints ).find(( gp: any ) =>
                            ( gpXe > 0 && gpXe < 1 && gp.y === gpYe ) ||
                            ( gp.x === gpXe && gpYe > 0 && gpYe < 1 ));

                    const toEp = connector.getToEndpoint( diagram as any );
                    if ( gpend && toEp.gluepoint ) {
                        ( connector as any ).toGPAutolayout = `${toShape.id}-${( gpend as any ).id}`;
                    }

                    if ( isEmpty( fromShape.__sakota__.getChanges())
                        || isEmpty( toShape.__sakota__.getChanges())) {
                        return;
                    }
                });


                result.children.forEach(  s => {
                    const shape = shapes.find(( sh: any ) => sh.id === s.id );
                    const restricted = Layouting.restriction.point({
                        x: relativeX + s.x,
                        y: relativeY + s.y,
                    }, [ 'GridService' ]);
                    shape.x = restricted.x;
                    shape.y = restricted.y;
                });

                // Correct elk layered algorithm's output to support creately
                if ( layoutingData.options[ 'org.eclipse.elk.algorithm' ].includes( 'layered' )) {
                    const direction = layoutingData.options[ 'org.eclipse.elk.direction' ];
                    Layouting.correctLayeredLayouting( result.edges, diagram, direction );
                }

                return Object.values(( diagram as any ).shapes );
            } catch ( error ) {
                Logger.error( 'Layouting failed:', error );
            }
        } else if ( layoutingData.source === 'd3' ) {
            Layouting[ layoutingData.algorithm ]( diagram, shapes, bounds, layoutingData, padding );
            return shapes;
        } else {
            Layouting.applyNative( shapes, bounds, layoutingData, diagram );
            shapes.forEach( s =>  {
                const { x, y, scaleX, scaleY } = Layouting.restriction.shapeTransform( s, {
                    x: s.x,
                    y: s.y,
                    scaleX: s.scaleX,
                    scaleY: s.scaleY,
                });
                s.x = x;
                s.y = y;
                s.scaleX = scaleX;
                s.scaleY = scaleY;
            });
            return shapes;
        }
    }

    /**
     * This method corrects the elk 'layered' layouting for creately.
     * Creately shapes have top,left,bottom,right main glupoints for the most shapes.
     * but elk 'layered' layouting considers that shapes only have gluepoints ( ports ) in
     * two sides which face the layout direction.
     * NOTE: coudn't resolve this with elk options
     *
     * What this method does is, if a particular has two out going connectors connected to
     * the same gluepoint, we switch the gluepoint of one connecter to other nearest glupoint.
     * That connector is selected based on the 'end' point location.
     */
    // tslint:disable-next-line: member-ordering
    protected static correctLayeredLayouting( edges, diagram: IDiagramDefinition, direction ) {
        // find edges with same source
        const sourceEdges = {};
        edges.forEach( edge => {
            if ( !sourceEdges[ edge.sources[ 0 ]]) {
                sourceEdges[ edge.sources[ 0 ]] = [];
            }
            sourceEdges[ edge.sources[ 0 ]].push( edge );
        });
        Object.keys( sourceEdges ).forEach( key => {
            if ( sourceEdges[ key ].length > 1 ) { // This shape has multiple outgoing edges
                const eds =  sourceEdges[ key ];
                eds.forEach( c => {
                    const shape = ( diagram as any ).shapes[ key ];
                    const connector = ( diagram as any ).shapes[ c.id ];
                    const gps = shape.getAvailableGluepoints( diagram );
                    const topGp = gps.sort(( a, b ) => a.y - b.y )[ 0 ].id;
                    const bottomGp = gps.sort(( a, b ) => b.y - a.y )[ 0 ].id;
                    const leftGp = gps.sort(( a, b ) => a.x - b.x )[ 0 ].id;
                    const rightGp = gps.sort(( a, b ) => b.x - a.x )[ 0 ].id;

                    let fromGPAutolayout;
                    if ( direction === 'RIGHT' || direction === 'LEFT' ) {
                        if ( c.sections[ 0 ].startPoint.y - c.sections[ 0 ].endPoint.y  > 50 ) {
                            fromGPAutolayout = `${key}-${topGp}`;
                        } else if ( c.sections[ 0 ].endPoint.y - c.sections[ 0 ].startPoint.y > 50 ) {
                            fromGPAutolayout = `${key}-${bottomGp}`;
                        }
                    } else if ( direction === 'DOWN'  || direction === 'UP' ) {
                        if ( c.sections[ 0 ].endPoint.x - c.sections[ 0 ].startPoint.x > 50 ) {
                            fromGPAutolayout = `${key}-${rightGp}`;
                        } else if ( c.sections[ 0 ].startPoint.x - c.sections[ 0 ].endPoint.x > 50 ) {
                            fromGPAutolayout = `${key}-${leftGp}`;
                        }
                    }

                    if ( fromGPAutolayout ) {
                        connector.triggerAutolayout = Math.random();
                        connector.fromGPAutolayout = fromGPAutolayout;
                    }
                });
            }
        });

    }

    // tslint:disable-next-line: member-ordering
    public static async getNextItems(
        all: { children: any, edges: any },
        current: { children: any },
        layoutingData,
        limit = 50,
    ) {
        const graph = {
            id: 'root', // not important
            children: all.children,
            edges: all.edges,
            properties: {
                'org.eclipse.elk.padding': `` + 0,
                'org.eclipse.elk.rectpacking.widthApproximation.strategy': 'TARGET_WIDTH',
                ...layoutingData as any,
            } as any,
        } as any;

        const result = await Layouting.elk.layout( graph );
        const _shapes = result.children.sort(( a, b ) => a.y - b.y );

        const levels = {};
        _shapes.forEach(( s, i ) => {
            if ( levels[ s.y ]) {
                levels[ s.y ].push( s );
            } else {
                levels[ s.y ] = [ s ];
            }
        });
        const shapes = [];
        Object.keys( levels ).forEach( key => {
            levels[ key ].sort(( a, b ) => a.x - b.x );
            shapes.push( ...levels[ key ]);
        });

        const conns = result.edges;
        const remaining = shapes.filter( s => !( current.children || []).find( c => c.id === s.id ));
        const nextSet = remaining.slice( 0, limit );
        const edges = [];
        nextSet.forEach( s => {
            const edge = conns.find( c => c.targets?.[0] === s.id );
            if ( edge ) {
                edges.push( edge );
            }
        });
        return {
            children: nextSet,
            edges,
        };
    }

    // tslint:disable-next-line: member-ordering
    public static getNextTreeNodes<T extends { id: string, parentId: string | null }>(
        all: Array<T>,
        nodeId?: string,
        levelsLimit = 3,
        nodesLimit = 100,
        exclude: string[] = [],
    ): Array<T> {

        const treeUtils = new TreeUtils({
            idProp: 'id', // the key of a unique identifier for an object (source object)
            parentIdProp: 'parentId', // the key of a unique parent identifier (source object)
            childrenProp: 'children', // the key, where child nodes are stored (destination object tree)
        });

        let output = [];
        const getChildrenRecursive = ( treeObj: any, level = 0 ) => {
            if ( level > levelsLimit  ) {
                return;
            }
            output = output.concat( treeObj.map( v => omit( v, [ 'children' ])));
            treeObj.forEach( node => {
                getChildrenRecursive( node.children, level + 1 );
            });
        };

        const tree = treeUtils.list2Tree( all );
        if ( !nodeId ) { // Start from root nodes
            getChildrenRecursive( tree );
        } else {
            const node = treeUtils.findTreeNodeById( tree, nodeId );
            // const list = treeUtils.findAllChildrenNodes( tree, nodeId ).map( v => ({
            //     id: v.id,
            //     parentId: v.parentId || null,
            // }));
            // list.push({ id: nodeId, parentId: null });
            // const _tree = treeUtils.list2Tree( list );
            getChildrenRecursive([ node ]);
            output.shift();
        }
        return output.filter( n => !exclude.includes( n.id )).slice( 0, nodesLimit );
    }

    /**
     * Convert shapes to ELK nodes
     */
    protected static prepareNodesForElk( diagram, shapes: any[], direction ): any {
        const children = shapes
            .filter( s => !s.isConnector())
            .map( s => ({
                id: s.id,
                width: s.width,
                height: s.height,
                x: s.x,
                y: s.y,
            }));
        const edges = [];
        const ignoredEdges = [];
        shapes.filter( s => !s.isConnector()).forEach( shape => {
            // shape as source
            const allConnectors = diagram.getConnectorsOfShape( shape.id );
            ignoredEdges.push( ...allConnectors.filter( c => c.connector.excludeInLayouting ).map( c => c.connector ));
            const sourceConnections = allConnectors
            .filter( c => !c.connector.excludeInLayouting )
            .filter( c => {
                // 'connection' is udefined for some diagrams
                const connection = c.connector.getConnection( diagram );
                const fromEndpoint = c.connector.getFromEndpoint( diagram );
                if ( connection && connection.shapeA.shapeId === shape.id ) {
                    return true;
                } else if ( !connection && fromEndpoint.shape?.id === shape.id ) {
                    return true;
                }
            }).map( c => c.connector );
            if ( sourceConnections.length ) {
                sourceConnections.forEach(( c: any ) => {
                    const src = shape.id;
                    const conn = c.getConnection( diagram );
                    const toEndpoint = c.getToEndpoint( diagram );
                    const fromEndpoint = c.getFromEndpoint( diagram );
                    const hId = diagram.shapes[ c.id ].path.headId;
                    const tId = diagram.shapes[ c.id ].path.tailId;
                    const head = diagram.shapes[ c.id ].path[ hId ];
                    const tail = diagram.shapes[ c.id ].path[ tId ];
                    head.gluepointLocked = false;
                    tail.gluepointLocked = false;
                    toEndpoint.gluepointLocked = false;
                    fromEndpoint.gluepointLocked = false;
                    const target = conn ? conn.shapeB.shapeId :
                        toEndpoint.shape ? toEndpoint.shape.id : null;
                    if ( target && shapes.find( s => s.id === src )
                        && shapes.find( s => s.id === target )) { // An edge should connect 2 shapes


                            const trgt = conn ? conn.shapeB.shapeId : toEndpoint.shape.id;

                            // setParentId for shapes
                            const sourcNode = children.find( s => s.id === shape.id );
                            const targetNode: any = children.find( s => s.id === trgt );

                            if ( targetNode && sourcNode ) {
                                targetNode.parentId = sourcNode.id;
                            }

                            const connectorText = ( c as ConnectorModel ).primaryTextModel;

                            const labels = [];
                            if ( connectorText ) {
                                labels.push({
                                    text: connectorText.plainText,
                                    width: connectorText.width,
                                    height: connectorText.height,
                                });
                            }

                            edges.push({
                                id: c.id,
                                sources: [ shape.id ],
                                targets: [ trgt ],
                                ...c.bounds,
                                labels
                            });
                    }
                });
            }
        });
        if ( direction === 'LEFT' || direction === 'RIGHT' ) {
            edges.sort(( a, b ) => a.top - b.top );
        } else {
            edges.sort(( a, b ) => a.left - b.left );
        }
        children.sort(( a, b ) => a.x - b.x );
        children.sort(( a, b ) => a.y - b.y );

        // const list = [];
        // children.forEach(( c, i ) => {
        //     const edg = edges.find( e => e.targets[0] === c.id );
        //     list.push({
        //         id: c.id,
        //         parentId: edg?.sources[0],
        //     });
        // });

        return {
            children,
            edges,
            ignoredEdges,
        };
    }

    /**
     * After the layouting operation done, the shapes should be pan to the top left of
     * the bounds specified
     */
    protected static panToCenter( shapes: IShapeModel[], bounds: IRectangle, layoutingData: ILayoutingData ) {
        if ( !shapes.length ) {
            return shapes;
        }
        let mergedBounds = new Rectangle( shapes[ 0 ].x, shapes[ 0 ].y, shapes[ 0 ].width, shapes[ 0 ].height );
        shapes.forEach(( s, i ) => {
            if ( i > 0 ) {
                mergedBounds = mergedBounds.absorb(
                    new Rectangle( shapes[ i ].x, shapes[ i ].y, shapes[ i ].width, shapes[ i ].height ),
                );
            }
        });
        const deltaX = mergedBounds.x + mergedBounds.width / 2 -
            ( bounds.x + bounds.width / 2 );
        const deltaY = mergedBounds.y - ( bounds.y + layoutingData.padding[0]);
        shapes.forEach(( s, i ) => {
            shapes[ i ].x = shapes[ i ].x - deltaX;
            shapes[ i ].y = shapes[ i ].y - deltaY;
        });
        return shapes;
    }

    protected static applyNative( shapes: IShapeModel[], bounds: IRectangle, layoutData: ILayoutingData, d ) {
        if ( Layouting[ layoutData.algorithm ] && typeof this[ layoutData.algorithm ] === 'function' ) {
            return Layouting[ layoutData.algorithm ]( shapes, bounds, layoutData, d );
        }
        return shapes;
    }


    /**
     * ----------  Native layouting implementations ------------------------
     */

    /**
     * simpleVerticalList
     */

    protected static free( shapes, bounds: IRectangle, ld, diagram ) {
        shapes.forEach( shape => {
            if ( !shape.containerId ) {
                return;
            }
            const container = diagram.shapes[ shape.containerId ];
            const region = container?.containerRegions?.[ shape.containerRegionId ];
            if ( region ) {
                const regionShapeData = region.shapes[ shape.id ];
                if ( regionShapeData && typeof regionShapeData === 'object' ) {
                    const { relativeX, relativeY } = regionShapeData;
                    shape.x = container.x + region.x + ( relativeX || Layouting.defaultPadding[0]);
                    shape.y = container.y + region.y + ( relativeY || Layouting.defaultPadding[3]);
                }
            }

        });

        // Do nothing;
    }

    protected static mindmapStandard( shapes, bounds: IRectangle, ld, diagram ) {
        const rootShape = shapes.find(( shape: any ) => {
            // map for mindmap
            if ( shape.type !== 'connector' ) {
                return diagram.getConnectorsOfShape( shape.id ).every( c => {
                    const toEndpoint = c.connector.getToEndpoint( diagram );
                    return toEndpoint.shape?.id !== shape.id;
                });
            }
            return false;
        });

        if ( !rootShape ) {
            return;
        }

        const rootNode = {
            id: rootShape.id,
            children: [],
            width: rootShape.width,
            height: rootShape.height,
            x: rootShape.x,
            y: rootShape.y,
            hgap: 80,
            vgap: 20,
        };

        const rec = ( root: any ) => {
            const s = diagram.shapes[ root.id ];
            diagram.getConnectorsOfShape( s.id ).forEach( c => {
                ( c.connector as any ).$$resetGluepoints = true;
                c.connector.getPoints().forEach(( p: any ) => delete p.gluepointId );
                const toEndpoint = c.connector.getToEndpoint( diagram );
                if ( toEndpoint.shape?.id && toEndpoint.shape?.id !== root.id ) {
                    const cNode = {
                        id: toEndpoint.shape.id,
                        width: toEndpoint.shape.width,
                        height: toEndpoint.shape.height,
                        x: toEndpoint.shape.x,
                        y: toEndpoint.shape.y,
                        hgap: 80,
                        vgap: 20,
                        children: [],
                    };
                    c.connector.triggerAutolayout = Math.random();
                    root.children.push( cNode );
                    rec( cNode );
                }
            });
        };
        rec( rootNode );

        const layout = new MindmapLayouts.Standard( rootNode, {}); // root is tree node like above
        const _rootNode = layout.doLayout();


        const traverse = ( node: any ) => {
           const shape = diagram.shapes[ node.id ];
           shape.x = bounds.x + node.x;
           shape.y = bounds.y + node.y;
           node.children.forEach( child => {
               traverse( child );
           });
        };
        traverse( _rootNode );
    }

    protected static simpleVerticalList( shapes, bounds: IRectangle, layoutData: ILayoutingData ) {
        const shapesArray = values( shapes ).filter( s => !s.isConnector());
        const orderedVertically = sortBy( shapesArray, s => s.y );
        orderedVertically.forEach(( current: IShapeModel, i ) => {
            if ( i === 0 ) {
                const currentRectWithImage = current.getBoundsWithImages();
                let imagePadding = 0;
                if ( currentRectWithImage.height >  Math.round( current.height )) {
                    imagePadding = currentRectWithImage.height - current.height;
                }
                current.y = bounds.y + imagePadding + layoutData.padding[0];
                if ( layoutData.options.nodeBehavior === 'fill' ) { // fit to the region width padding
                    const effectiveWidth = ( bounds.width - layoutData.padding[3] - layoutData.padding[1]);
                    if ( current.minBounds && ( current.minBounds.width > effectiveWidth )) {
                        current.scaleX = current.minBounds.width / current.defaultBounds.width;
                    } else {
                        current.scaleX = effectiveWidth / current.defaultBounds.width;
                    }
                    current.autoResizeWidth = false;
                    const fixedAspectRatio = ( current.transformSettings || {} as any ).fixAspectRatio;
                    if ( fixedAspectRatio ) {
                        current.scaleY = current.scaleX;
                    }
                    current.x = bounds.x + layoutData.padding[3];
                } else if ( layoutData.options.nodeBehavior === 'center' ) { // center in the region
                    current.x = bounds.x + (
                        bounds.width / 2  -  current.width / 2
                    );
                } else {
                    current.x = bounds.x + layoutData.padding[3];
                }
            }
            const prev = orderedVertically[ i - 1 ];
            if ( prev ) {
                const currentRectWithImage = current.getBoundsWithImages();
                let imagePadding = 0;
                if ( currentRectWithImage.height > current.height ) {
                    imagePadding = currentRectWithImage.height - current.height;
                }
                current.y = prev.y + prev.height + imagePadding + layoutData.spacing[ 0 ];
                if ( layoutData.options.nodeBehavior === 'fill' ) { // fit to the region width padding
                    if ( current.minBounds && ( current.minBounds.width > prev.width )) {
                        current.scaleX = current.minBounds.width / current.defaultBounds.width;
                    } else {
                        current.scaleX = prev.width / current.defaultBounds.width;
                    }
                    current.autoResizeWidth = false;
                    const fixedAspectRatio = ( current.transformSettings || {} as any ).fixAspectRatio;
                    if ( fixedAspectRatio ) {
                        current.scaleY = current.scaleX;
                    }
                    current.x = prev.x;
                } else if ( layoutData.options.nodeBehavior === 'center' ) { // center in the region
                    current.x = bounds.x + (
                        bounds.width / 2  -  current.width / 2
                    );
                } else {
                    current.x = prev.x;
                }
            }
        });
        return shapes;
    }

    /**
     * ----------  Native layouting implementations ------------------------
     */

    /**
     * simpleBoxLayout
     */
    protected static simpleBoxLayout( shapes, bounds: IRectangle, layoutData: ILayoutingData ) {
        const shapesArray = values( shapes );
        const orderedHorizontally = sortBy( shapesArray, [ 'x', 'y' ]);
        const maxColumnWidth = layoutData.options.maxWidth;
        const maxShapeWidth = maxBy( orderedHorizontally, s => s.width )!.width;
        let columnsCounter = 0;
        const columnBounds = {
            x: bounds.x + layoutData.padding[3],
            y: bounds.y + layoutData.padding[0],
            width: maxShapeWidth > maxColumnWidth ? maxColumnWidth : maxShapeWidth,
            height: bounds.height - layoutData.padding[0] - layoutData.padding[2],
        };
        orderedHorizontally.forEach(( current: IShapeModel, i ) => {
            if ( columnsCounter >=  layoutData.options.maxVerticalDepth ) {
                const currentRectWithImage = current.getBoundsWithImages();
                let imagePadding = 0;
                if ( currentRectWithImage.height > Math.round( current.height )) {
                    imagePadding = currentRectWithImage.height - current.height;
                }
                columnsCounter = 1;
                columnBounds.x = columnBounds.x + columnBounds.width + layoutData.spacing[ 1 ];
                columnBounds.y = bounds.y + imagePadding + layoutData.padding[0];
            } else {
                columnsCounter++;
            }
            if ( columnsCounter === 1 ) {
                    const currentRectWithImage = current.getBoundsWithImages();
                    let imagePadding = 0;
                    if ( currentRectWithImage.height > current.height ) {
                        imagePadding = currentRectWithImage.height - current.height;
                    }
                    current.x = columnBounds.x;
                    current.y = columnBounds.y + imagePadding;
                    current.scaleX = columnBounds.width / current.defaultBounds.width;
            }
            const prev = orderedHorizontally[ i - 1 ];
            if ( prev && columnsCounter !== 1 ) {
                const currentRectWithImage = current.getBoundsWithImages();
                let imagePadding = 0;
                if ( currentRectWithImage.height > current.height ) {
                    imagePadding = currentRectWithImage.height - current.height;
                }
                current.x = columnBounds.x;
                current.y = prev.y + prev.height + imagePadding + layoutData.spacing[ 0 ];
                if ( current.minBounds && ( current.minBounds.width > prev.width )) {
                    current.scaleX = current.minBounds.width / current.defaultBounds.width;
                } else {
                    current.scaleX = prev.width / current.defaultBounds.width;
                }
            }
        });
        return shapes;
    }

    /**
     * layout base on the layoutIndex property.
     */
    protected static indexBaseVerticalListLayout( shapes, bounds: IRectangle, layoutData: ILayoutingData ) {
        const shapesArray = values( shapes );
        const orderedVertically = sortBy( shapesArray, s => s.layoutIndex );
        orderedVertically.forEach(( current: IShapeModel, i ) => {
            if ( i === 0 ) {
                const currentRectWithImage = current.getBoundsWithImages();
                let imagePadding = 0;
                if ( currentRectWithImage.height >  Math.round( current.height )) {
                    imagePadding = currentRectWithImage.height - current.height;
                }
                current.y = bounds.y + imagePadding + layoutData.padding[0];
                if ( layoutData.options.nodeBehavior === 'fill' ) { // fit to the region width padding
                    const effectiveWidth = ( bounds.width - layoutData.padding[3] - layoutData.padding[1]);
                    if ( current.minBounds && ( current.minBounds.width > effectiveWidth )) {
                        current.scaleX = current.minBounds.width / current.defaultBounds.width;
                    } else {
                        current.scaleX = effectiveWidth / current.defaultBounds.width;
                    }
                    current.autoResizeWidth = false;
                    const fixedAspectRatio = ( current.transformSettings || {} as any ).fixAspectRatio;
                    if ( fixedAspectRatio ) {
                        current.scaleY = current.scaleX;
                    }
                    current.x = bounds.x + layoutData.padding[3];
                } else if ( layoutData.options.nodeBehavior === 'center' ) { // center in the region
                    current.x = bounds.x + (
                        bounds.width / 2  -  current.width / 2
                    );
                } else {
                    current.x = bounds.x + layoutData.padding[3];
                }
            }
            const prev = orderedVertically[ i - 1 ];
            if ( prev ) {
                const currentRectWithImage = current.getBoundsWithImages();
                let imagePadding = 0;
                if ( currentRectWithImage.height > current.height ) {
                    imagePadding = currentRectWithImage.height - current.height;
                }
                current.y = prev.y + prev.height + imagePadding + layoutData.spacing[ 0 ];
                if ( layoutData.options.nodeBehavior === 'fill' ) { // fit to the region width padding
                    if ( current.minBounds && ( current.minBounds.width > prev.width )) {
                        current.scaleX = current.minBounds.width / current.defaultBounds.width;
                    } else {
                        current.scaleX = prev.width / current.defaultBounds.width;
                    }
                    current.autoResizeWidth = false;
                    const fixedAspectRatio = ( current.transformSettings || {} as any ).fixAspectRatio;
                    if ( fixedAspectRatio ) {
                        current.scaleY = current.scaleX;
                    }
                    current.x = prev.x;
                } else if ( layoutData.options.nodeBehavior === 'center' ) { // center in the region
                    current.x = bounds.x + (
                        bounds.width / 2  -  current.width / 2
                    );
                } else {
                    current.x = prev.x;
                }
            }
        });
        return shapes;
    }


    /**
     * nestedSnapToCenter
     */
    protected static nestedSnapToCenter( shapes, bounds: IRectangle, layoutData: ILayoutingData ) {
        let shapesArray = values( shapes );

        values( shapes ).forEach( parent => {
            const children = shapesArray.filter( shape => shape !== parent
                && shape.y < parent.y + parent.height && shape.y > parent.y );
            shapesArray = shapesArray.filter( s => !children.includes( s ));
        });

        const nestedShapes = {};
        shapesArray.forEach( shape => {
            Object.assign( nestedShapes, this.buildNestedStructure( values( shapes ), shape ));
        });

        const initX = bounds.x + ( bounds.width / 2 );
        this.drawNestedStructure( values( shapes ), nestedShapes, initX );

        return shapes;
    }

    /** Helpers for nestedSnapToCenter for recursive calls */
    protected static buildNestedStructure( shapes, parentShape ) {
        const nestedStructure = {};
        if ( !shapes ) {
            return { [parentShape.id] : nestedStructure };
        }
        const childShapes = shapes.filter( shape => shape !== parentShape
            && shape.y < parentShape.y + parentShape.height && shape.y > parentShape.y );
        const nextLevel = [];
        childShapes.forEach( child => {
            const nest = this.buildNestedStructure( shapes, child );
            nextLevel.push( ...Object.keys( nest[ child.id ]));
            Object.assign( nestedStructure, nest );

        });
        const overlap = intersection( nextLevel, Object.keys( nestedStructure ));
        overlap.forEach( key => delete nestedStructure[key]);
        return { [parentShape.id] : nestedStructure };
    }

    protected static drawNestedStructure( shapes, nest, nextXValue, children? ) {
        if ( !nest ) {
            return;
        }
        Object.keys( nest ).forEach( sid => {
            const shape = shapes.find( item => item.id === sid );
            shape.x = nextXValue - ( shape.width / 2 );
            this.drawNestedStructure( shapes, nest[sid], nextXValue + ( shape.width / 2 ), true );
        });
    }

    protected static treeDownComapct(
        diagram: DiagramModel,
        shapes: ShapeModel[], 
        bounds: IRectangle,
        layoutData: ILayoutingData,
    ) {

        let _bounds = bounds;
        Layouting.prepareNodesForD3( diagram, shapes, 'DOWN').forEach( set =>  {
            const children = set.nodes;
            const edges = set.links;

            if ( !children?.length || !edges?.length ) {
                return;
            }
            const opt = layoutData.options as any ;
            const chart = new OrgChart<any>()
                .nodeHeight( d => d.data.height )
                .nodeWidth( d => d.data.width )
                .childrenMargin( d => opt.childrenMargin )
                .compactMarginBetween( d => opt.compactMarginBetween )
                .compactMarginPair( d  => opt.compactMarginPair )
                .neighbourMargin(( a, b ) => opt.neighbourMargin )
                .siblingsMargin(() => opt.siblingsMargin )
                .data( children )
                .nodeId( d => d.id ) 
                .parentNodeId( d => d.parentId ) 
                .compact( true )
                .render().expandAll();
            const attr = chart.getChartState();
            attr.svg.node().remove();
    
            const allNodes = chart.getChartState().allNodes;
            const padding = layoutData.padding;
            let minX = ( diagram.shapes[ allNodes[0].id ] as ShapeModel ).x;
            let minY = ( diagram.shapes[ allNodes[0].id ] as ShapeModel ).y;
            allNodes.map(node => {
                const relativeX = _bounds.x;
                const relativeY = _bounds.y;
                const shape = shapes.find(( sh: any ) => sh.id === node.data.id );
                const restricted = Layouting.restriction.point({
                    x: relativeX + node.x,
                    y: relativeY + node.y,
                }, [ 'GridService' ]);
                shape.x = restricted.x;
                shape.y = restricted.y;
                minX = Math.min( minX, shape.x );
                minY = Math.min( minY, shape.y );
    
    
                // If fromshape has more than one children category children by their x position
                if ( node.children?.length > 1 ) {
                    const cols = {};
                    node.children.forEach( c => {
                        const x = c.x;
                        if ( !cols[ `${x}` ] ) {
                            cols[ `${x}` ] = [];
                        }
                        cols[ `${x}` ].push( c );
                    });
                    Object.keys( cols ).map( key => Number( key ))
                        .sort( ( a, b ) => a - b ).forEach(( col, i ) => {
                            cols[ `${col}` ].forEach(( c => {
                                c.colNumber = i;
                                c.totalCols = Object.keys( cols ).length;
                            }));
                        });
                }
                return node.data;
            });
    
            const diffX = minX - _bounds.x;
            const diffY = minY - _bounds.y;
            let maxY = ( diagram.shapes[ allNodes[0].id ] as ShapeModel ).y;
            allNodes.map( node => {
                const shape = shapes.find(( sh: any ) => sh.id === node.id );
                shape.x -= ( diffX - padding[ 3 ]);
                shape.y -= ( diffY - padding[ 0 ]);
                maxY = Math.max( maxY, shape.y + shape.height );
            });
    
            edges.forEach(  c => {
                const connectorId = c.id;
                const connectorModel = ( diagram as any ).shapes[ connectorId ] as ConnectorModel;
                const fromShapeId = c.from;
                const toShapeId = c.to;
                const toShape = shapes.find(( sh: any ) => sh.id === toShapeId );
                const gpIdBottom = GluePointModel.getGluePointBySide( 'bottom', toShape );
                connectorModel.fromGPAutolayout = `${fromShapeId}-${gpIdBottom}`;
                const toShapeNode: any = allNodes.find(( sh: any ) => sh.data.id === toShapeId );
    
                const gpIdLeft = GluePointModel.getGluePointBySide( 'left', toShape );
                const gpIdright = GluePointModel.getGluePointBySide( 'right', toShape );
                const gpIdTop = GluePointModel.getGluePointBySide( 'top', toShape );
                
                connectorModel.fromGPAutolayout = `${fromShapeId}-${gpIdBottom}`; 
                if ( toShapeNode.firstCompactNode && !toShapeNode.compactEven ) {
                    connectorModel.toGPAutolayout = `${toShapeId}-${gpIdLeft}`;    
                } else if ( toShapeNode.firstCompactNode && toShapeNode.compactEven ) {
                    connectorModel.toGPAutolayout = `${toShapeId}-${gpIdright}`;    
                } else {
                    connectorModel.toGPAutolayout = `${toShapeId}-${gpIdTop}`;    
                }
    
                if ( toShapeNode.totalCols % 2 === 1 && toShapeNode.colNumber === toShapeNode.totalCols - 1 ) {
                    connectorModel.toGPAutolayout = `${toShapeId}-${gpIdLeft}`;
                }
    
                ( connectorModel as any ).shapeOverlapPadding = ( opt.compactMarginBetween / 2 ) + 2;
                ( connectorModel as any ).$$noShapeOverlapIds = children.map( c => c.id );
                ( connectorModel as any ).triggerAutolayout = Math.random();
            });
            _bounds = { x: bounds.x, y: maxY + opt.childrenMargin } as any;

        });

    }

    protected static prepareNodesForD3( diagram, shapes: any[], direction ): any {
        const children = shapes
            .filter( s => !s.isConnector())
            .map( s => ({
                id: s.id,
                width: s.width,
                height: s.height,
                x: s.x,
                y: s.y,
            }));
        const edgesMap = {};

        shapes.filter( s => !s.isConnector()).forEach( shape => {
            // shape as source
            const allConnectors = diagram.getConnectorsOfShape( shape.id );
            const sourceConnections = allConnectors
                .filter( c => {
                    // 'connection' is udefined for some diagrams
                    const connection = c.connector.getConnection( diagram );
                    const fromEndpoint = c.connector.getFromEndpoint( diagram );
                    if ( connection && connection.shapeA.shapeId === shape.id ) {
                        return true;
                    } else if ( !connection && fromEndpoint.shape?.id === shape.id ) {
                        return true;
                    }
                }).map( c => c.connector );
            if ( sourceConnections.length ) {
                sourceConnections.forEach(( c: any ) => {
                    const src = shape.id;
                    const conn = c.getConnection( diagram );
                    const toEndpoint = c.getToEndpoint( diagram );
                    const target = conn ? conn.shapeB.shapeId :
                        toEndpoint.shape ? toEndpoint.shape.id : null;
                    if ( target && shapes.find( s => s.id === src )
                        && shapes.find( s => s.id === target )) { // An edge should connect 2 shapes
                            const trgt = conn ? conn.shapeB.shapeId : toEndpoint.shape.id;
                            // setParentId for shapes
                            const sourcNode = children.find( s => s.id === shape.id );
                            const targetNode: any = children.find( s => s.id === trgt );
                            if ( targetNode && sourcNode ) {
                                targetNode.parentId = sourcNode.id;
                            }
                            edgesMap[ c.id ] = {
                                id: c.id,
                                from: shape.id,
                                to: trgt,
                            };
                    }
                });
            }
        });
        const edges = Object.values( edgesMap ) as any;
        children.sort(( a, b ) => a.x - b.x );
        children.sort(( a, b ) => a.y - b.y );

        // const list = [];
        // children.forEach(( c, i ) => {
        //     const edg = edges.find( e => e.targets[0] === c.id );
        //     list.push({
        //         id: c.id,
        //         parentId: edg?.sources[0],
        //     });
        // });
        const data = Layouting.getTrees( children, edges );
        return data;
    }

    protected static getTrees(nodes, links) {
        const nodeMap = new Map(nodes.map(node => [node.id, node]));
        const visited = new Set();
        const trees = [];
    
        function dfs(node, treeNodes, treeLinks, linkSet) {
            if (!node || visited.has(node.id)) return;
            visited.add(node.id);
            treeNodes.push(node);
            
            for (const link of links) {
                const isForward = link.from === node.id && nodeMap.has(link.to);
                const isBackward = link.to === node.id && nodeMap.has(link.from);
    
                if ((isForward || isBackward) && !linkSet.has(link)) {
                    treeLinks.push(link);
                    linkSet.add(link);
                    dfs(nodeMap.get(isForward ? link.to : link.from), treeNodes, treeLinks, linkSet);
                }
            }
        }
    
        // Find all connected trees
        for (const node of nodes) {
            if (!visited.has(node.id)) {
                const treeNodes = [];
                const treeLinks = [];
                const linkSet = new Set(); // Track unique links per tree
                dfs(node, treeNodes, treeLinks, linkSet);
                trees.push({ nodes: treeNodes, links: treeLinks });
            }
        }
    
        // Find isolated links (links not attached to any node)
        const processedLinks = new Set();
        for (const link of links) {
            const isOrphan = !nodeMap.has(link.from) && !nodeMap.has(link.to);
            if (isOrphan && !processedLinks.has(link)) {
                trees.push({ nodes: [], links: [link] });
                processedLinks.add(link);
            }
        }
    
        return trees;
    }

    protected static getConnectorPoints(parent, child, layout = "top-to-bottom") {
        if (layout === "top-to-bottom") {
          return {
            from: Layouting.getEdgePoint(parent, "bottom"),
            to: Layouting.getEdgePoint(child, "left")
          };
        } else if (layout === "left-to-right") {
          return {
            from: Layouting.getEdgePoint(parent, "right"),
            to: Layouting.getEdgePoint(child, "left")
          };
        }
      }

    protected static getEdgePoint(node, position) {
        const edges = {
          top: { x: node.x, y: node.y - node.height / 2 },
          bottom: { x: node.x, y: node.y + node.height / 2 },
          left: { x: node.x - node.width / 2, y: node.y },
          right: { x: node.x + node.width / 2, y: node.y }
        };
        return edges[position];
    }

    // protected static buildHierarchy( flatNodes ) {
    //     const idMap = new Map( flatNodes.map(d => [d.id, { ...d, children: [] }]));
    //     let root = null;
    //     flatNodes.forEach(d => {
    //       if (d.parentId) {
    //         ( idMap.get(d.parentId) as any ).children.push(idMap.get(d.id));
    //       } else {
    //         root = idMap.get(d.id);
    //       }
    //     });
    //     return d3.hierarchy(root);
    // }

}
